import { useId, useMemo, useRef, useState } from "react";
import { Building2, FolderKanban, ChevronDown, ChevronRight, ImagePlus, Layers3, MapPin, Plus, Search, Trash2, Cog, Nut, QrCode } from "lucide-react";
import { useTranslation } from "react-i18next";
import FacilityQrModal from "./FacilityQrModal";
import { postProjectNodeImage, deleteProjectNodeImage, postProjectNodes } from "lib/api/projectStructure";
import { PROJECT_PATH_FIELDS, emptyProjectPath, normalizeProjectPaths, projectNodePaths, projectPathLabel } from "lib/utils/projectNodePaths";
import "css/projectSettings.css";
import ProjectTreeImageEditor from "./ProjectTreeImageEditor";
import ProjectNodeDeleteButton from "./ProjectNodeDeleteButton";

const NODE_PRESENTATION = {
  building: { label: "건물", icon: Building2 },
  floor: { label: "층", icon: Layers3 },
  location: { label: "위치", icon: MapPin },
  facility: { label: "설비명", icon: Cog },
  part: { label: "부품명", icon: Nut },
};

function ProjectTreeImageManager({ projectId, projectName, nodes, paths, onRegistered, disabled }) {
  const { t } = useTranslation();
  const [qrNode, setQrNode] = useState(null);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState("");
  const deletingRef = useRef(false);
  const removeImage = async node => {
    if (disabled || busyId || registering || deletingRef.current) return;
    if (!window.confirm(`${node.name} 대표 이미지를 삭제할까요? 실제 이미지 파일이 삭제되며 복구할 수 없습니다. 등록된 경로와 업로드 파일은 유지됩니다.`)) return;
    deletingRef.current = true; setBusyId(node.id); setMessage("");
    try {
      const result = await deleteProjectNodeImage(projectId, node.id, node.image_path);
      setMessage(result.message);
      if (result.success) await onRegistered?.(result.data.nodes);
    } catch { setMessage("삭제 결과 또는 목록 갱신을 확인하지 못했습니다. 다시 조회하세요."); }
    finally { deletingRef.current = false; setBusyId(""); }
  };
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState(null);
  const [newNode, setNewNode] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const pathById = useMemo(() => new Map(paths.map(path => [path.id, path])), [paths]);
  const visibleNodes = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko");
    return nodes.filter(node => NODE_PRESENTATION[node.node_type] && pathById.has(node.id))
      .filter(node => !keyword || projectPathLabel(pathById.get(node.id)).toLocaleLowerCase("ko").includes(keyword));
  }, [nodes, pathById, query]);
  const childrenById = useMemo(() => {
    const visibleIds = new Set(visibleNodes.map(node => node.id));
    const byId = new Map(nodes.map(node => [node.id, node]));
    visibleNodes.forEach(node => {
      let parent = byId.get(node.parent_id);
      const seen = new Set([node.id]);
      while (parent && !seen.has(parent.id)) {
        seen.add(parent.id); visibleIds.add(parent.id); parent = byId.get(parent.parent_id);
      }
    });
    const children = new Map();
    nodes.filter(node => visibleIds.has(node.id) && pathById.has(node.id)).forEach(node => {
      const parentId = visibleIds.has(node.parent_id) ? node.parent_id : null;
      if (!children.has(parentId)) children.set(parentId, []);
      children.get(parentId).push(node);
    });
    children.forEach(items => items.sort((a, b) => a.name.localeCompare(b.name, "ko", { numeric: true })));
    return children;
  }, [nodes, visibleNodes, pathById]);
  const startAdding = (type, parent = null) => {
    setQuery(""); setMessage("");
    if (parent) setCollapsed(value => ({ ...value, [parent.id]: false }));
    setNewNode({ type, parentId: parent?.id ?? null, name: "", path: parent ? pathById.get(parent.id) : emptyProjectPath() });
  };
  const register = async event => {
    event.preventDefault();
    if (!newNode?.name.trim() || registering || disabled) return;
    const field = PROJECT_PATH_FIELDS.find(item => item.type === newNode.type);
    const values = normalizeProjectPaths([{ ...newNode.path, [field.key]: newNode.name, _nodeType: newNode.type }]);
    setRegistering(true); setMessage("");
    try {
      const result = await postProjectNodes(projectId, values);
      setMessage(result.message || (result.success ? "경로를 등록했습니다." : "등록에 실패했습니다."));
      if (result.success) {
        setNewNode(null);
        await onRegistered?.(result.data.nodes);
      }
    } catch { setMessage("등록 또는 목록 갱신에 실패했습니다. 다시 확인해 주세요."); }
    finally { setRegistering(false); }
  };
  const renderNewNode = parentId => newNode?.parentId === parentId && <li className="projectTreeBranches__draft">
    <form onSubmit={register}>
      <span>{newNode.parentId ? projectPathLabel(newNode.path) : "프로젝트"} 아래에 추가</span>
      <label><span>새 {NODE_PRESENTATION[newNode.type].label}</span>
        <input autoFocus maxLength={newNode.type === "part" ? 255 : 100} required value={newNode.name} disabled={registering}
          placeholder={newNode.type === "building" ? "예: A동" : newNode.type === "floor" ? "예: 2층" : "예: 기계실"}
          onChange={event => setNewNode({ ...newNode, name: event.target.value })} />
      </label>
      <div><button type="submit" className="btn-bg-blue" disabled={disabled || registering || !newNode.name.trim()}>{registering ? "등록 중…" : "등록"}</button>
        <button type="button" className="projectSettings__secondary" disabled={registering} onClick={() => setNewNode(null)}>취소</button></div>
    </form>
  </li>;
  const renderBranches = parentId => <ul className="projectTreeBranches">
    {(childrenById.get(parentId) || []).map(node => {
      const presentation = NODE_PRESENTATION[node.node_type];
      const NodeIcon = presentation.icon;
      const hasChildren = Boolean(childrenById.get(node.id)?.length || newNode?.parentId === node.id);
      const expanded = Boolean(query) || !collapsed[node.id];
      const childTypes = PROJECT_PATH_FIELDS.slice(PROJECT_PATH_FIELDS.findIndex(field => field.type === node.node_type) + 1).map(field => field.type);
      return <li key={node.id}>
        <div className="projectTreeBranches__node">
          <ProjectNodeDeleteButton projectId={projectId} node={node} hasChildren={nodes.some(item => item.parent_id === node.id)} onDeleted={onRegistered} disabled={disabled || registering || Boolean(busyId)} />
          <button type="button" className="projectTreeBranches__qr" aria-label={`${node.name} QR 생성`} title="QR 생성"
            onClick={() => setQrNode(node)}><QrCode size={15} aria-hidden="true" /></button>
          {hasChildren && <button type="button" className="projectTreeBranches__toggle" aria-label={`${node.name} 하위 경로`} aria-expanded={expanded}
            onClick={() => setCollapsed(value => ({ ...value, [node.id]: expanded }))}>{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>}
          <div className="projectTreeBranches__identity">
          <label className={`projectTreeBranches__image ${node.image_url ? "has-image" : ""}`}>
            {node.image_url ? <img src={node.image_url} alt={`${node.name} 대표 이미지`} /> : <ImagePlus size={20} aria-hidden="true" />}
            <span>{busyId === node.id ? "저장 중…" : node.image_url ? "이미지 변경" : "이미지 등록"}</span>
            <input id={`tree-image-${node.id}`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label={`${node.name} 대표 이미지 등록`}
              disabled={disabled || Boolean(busyId) || registering}
              onChange={event => { const file = event.target.files?.[0]; event.target.value = ""; selectImage(node, file); }} />
          </label>
          <div className="projectTreeBranches__name"><small><span className="projectTreeBranches__leaf"><NodeIcon size={14} aria-hidden="true" /></span>{presentation.label}</small><strong title={node.name} tabIndex={0}>{node.name}</strong></div>
          </div>
          <div className="projectTreeBranches__actions">{node.image_url && <button type="button" className="projectSettings__secondary" disabled={disabled || registering || Boolean(busyId)} aria-label={`${node.name} 대표 이미지 삭제`} onClick={() => removeImage(node)}><Trash2 size={14} aria-hidden="true" />사진 삭제</button>}{childTypes.map(type => <button type="button" key={type} className="projectSettings__secondary"
            aria-label={`${node.name}에 ${NODE_PRESENTATION[type].label} 추가`} disabled={disabled || registering || Boolean(busyId)}
            onClick={() => startAdding(type, node)}><Plus size={14} aria-hidden="true" />{NODE_PRESENTATION[type].label}</button>)}</div>
        </div>
        {hasChildren && expanded && renderBranches(node.id)}
      </li>;
    })}
    {renderNewNode(parentId)}
  </ul>;

  const selectImage = (node, file) => {
    if (!file || busyId || disabled) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      setMessage("JPG, PNG, WebP, GIF 이미지만 업로드할 수 있습니다."); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage("대표 이미지는 10MB 이하만 업로드할 수 있습니다."); return;
    }
    setMessage("");
    setDraft({ node, file });
  };
  const upload = async (node, file) => {
    if (file.size > 10 * 1024 * 1024) return { success: false, message: "대표 이미지는 10MB 이하만 업로드할 수 있습니다. 저장 크기를 줄여주세요." };
    setBusyId(node.id);
    setMessage("");
    try {
      const result = await postProjectNodeImage(projectId, node.id, file);
      setMessage(result.message);
      if (result.success) {
        try { await onRegistered?.(result.data.nodes); }
        catch { setMessage("이미지는 저장됐지만 목록 갱신에 실패했습니다. 다시 조회해 주세요."); }
      }
      return result;
    } finally { setBusyId(""); }
  };

  return <div className="projectTreeImages">
    <div className="projectTreeImages__toolbar">
      <div>
        <strong>경로와 대표 이미지</strong>
        <p>항목 옆의 + 버튼으로 하위 경로를 추가하고, 사진 영역을 눌러 대표 이미지를 등록하세요.</p>
      </div>
      <label className="projectTreeImages__search">
        <Search size={16} aria-hidden="true" />
        <span className="sr-only">경로 검색</span>
        <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="현장 경로 검색" />
      </label>
    </div>
    <div className="projectTreeBranches__body" tabIndex={0} role="region" aria-label="현장 경로 편집">
    <div className="projectTreeBranches__canvas">
    <div className="projectTreeBranches__root"><FolderKanban size={18} aria-hidden="true" /><strong>{projectName}</strong>
      <div className="projectTreeBranches__actions">{PROJECT_PATH_FIELDS.map(({ type }) => <button key={type} type="button" className="projectSettings__secondary"
        disabled={disabled || registering || Boolean(busyId)} onClick={() => startAdding(type)}><Plus size={14} aria-hidden="true" />{NODE_PRESENTATION[type].label} 추가</button>)}</div>
    </div>
      {renderBranches(null)}
      {!visibleNodes.length && <p className="projectTreeImages__empty">{query ? "검색 결과가 없습니다." : "대표 이미지를 등록할 경로가 없습니다."}</p>}
    </div>
    </div>
    {message && <p role="status" className="projectSettings__message">{message}</p>}
    {qrNode && <FacilityQrModal metadata={{ ...pathById.get(qrNode.id), node_id: qrNode.id }} t={t} onClose={() => setQrNode(null)} />}
    {draft && <ProjectTreeImageEditor file={draft.file} nodeName={draft.node.name}
      onSave={file => upload(draft.node, file)} onClose={() => setDraft(null)} />}
  </div>;
}

export function ProjectTreePathEditor({ paths, onChange, nodes = [], disabled = false }) {
  const prefix = useId();
  const registered = useMemo(() => projectNodePaths(nodes), [nodes]);
  const addPath = (type) => {
    const previous = paths[paths.length - 1] ?? {};
    onChange([...paths, {
      ...emptyProjectPath(),
      _nodeType: type,
      ...Object.fromEntries(PROJECT_PATH_FIELDS.slice(0, PROJECT_PATH_FIELDS.findIndex(field => field.type === type)).map(field => [field.key, previous[field.key] || ""])),
    }]);
  };
  return <div className="projectTreeEditor">
    <div className="projectTreeEditor__paths">
      {paths.map((path, index) => {
        const type = path._nodeType || [...PROJECT_PATH_FIELDS].reverse().find(field => path[field.key])?.type || "building";
        const depth = PROJECT_PATH_FIELDS.findIndex(field => field.type === type);
        const field = PROJECT_PATH_FIELDS[depth];
        const { icon: NodeIcon, label } = NODE_PRESENTATION[type];
        const parentFields = PROJECT_PATH_FIELDS.slice(0, depth);
        const parentKey = (value) => JSON.stringify(parentFields.map(parent => String(value[parent.key] || "").trim()));
        const parentPath = Object.fromEntries(parentFields.map(parent => [parent.key, path[parent.key] || ""]));
        const candidates = [parentPath];
        for (const source of [...registered, ...normalizeProjectPaths(paths.filter((_, row) => row !== index))]) {
          for (let parentDepth = 0; parentDepth < depth; parentDepth++) {
            if (source[PROJECT_PATH_FIELDS[parentDepth].key]) candidates.push(Object.fromEntries(PROJECT_PATH_FIELDS.slice(0, parentDepth + 1).map(parent => [parent.key, source[parent.key]])));
          }
        }
        const parents = [...new Map(candidates.filter(candidate => parentFields.some(parent => candidate[parent.key]?.trim()))
          .map(candidate => [parentKey(candidate), candidate])).values()];
        const update = (patch) => onChange(paths.map((item, row) => row === index ? { ...item, _nodeType: type, ...patch } : item));
        return <div className="projectTreeEditor__path" key={index}>
          <div className="projectTreeEditor__kind"><NodeIcon size={16} aria-hidden="true" />{label} 추가</div>
          <div className="projectTreeEditor__fields">
            {depth > 0 ? <label htmlFor={`${prefix}-${index}-parent`}>
              <span>상위 경로</span>
              <select id={`${prefix}-${index}-parent`} disabled={disabled} value={parentKey(path)}
                onChange={event => {
                  const values = JSON.parse(event.target.value);
                  update(Object.fromEntries(parentFields.map((parent, i) => [parent.key, values[i]])));
                }}>
                <option value={parentKey({})}>프로젝트 바로 아래 (상위 경로 없음)</option>
                {parents.map(parent => <option key={parentKey(parent)} value={parentKey(parent)}>{projectPathLabel(parent)}</option>)}
              </select>
            </label> : <p className="projectTreeEditor__root">프로젝트 바로 아래에 건물을 추가합니다.</p>}
            <label htmlFor={`${prefix}-${index}-name`}>
              <span>새 {field.label}</span>
              <input id={`${prefix}-${index}-name`} maxLength={type === "part" ? 255 : 100} value={path[field.key] ?? ""}
                placeholder={type === "building" ? "예: A동" : type === "floor" ? "예: 2층" : "예: 기계실"}
                disabled={disabled} onChange={event => update({ [field.key]: event.target.value })} />
            </label>
          </div>
          <button type="button" className="projectTreeEditor__remove" disabled={disabled}
            aria-label={`${index + 1}번 입력 경로 삭제`} onClick={() => onChange(paths.filter((_, row) => row !== index))}>
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>;
      })}
    </div>
    <div className="projectTreeEditor__addActions" role="group" aria-label="추가할 항목">
      {PROJECT_PATH_FIELDS.map(({ type }) => <button key={type} type="button" className="projectSettings__secondary"
        disabled={disabled || paths.length >= 1000} onClick={() => addPath(type)}>
        <Plus size={16} aria-hidden="true" /> {NODE_PRESENTATION[type].label} 추가
      </button>)}
    </div>
  </div>;
}

export default function ProjectTreeManager({ projectId, projectName, nodes = [], onRegistered, onSelectPath, disabled = false }) {
  const [paths, setPaths] = useState([emptyProjectPath()]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const registered = useMemo(() => projectNodePaths(nodes), [nodes]);
  const save = async () => {
    const values = normalizeProjectPaths(paths);
    if (!values.length || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await postProjectNodes(projectId, values);
      setMessage(result.message);
      if (result.success) {
        setPaths([emptyProjectPath()]);
        if (values.length === 1) onSelectPath?.(values[0]);
        try { await onRegistered?.(result.data.nodes); }
        catch { setMessage("경로는 등록됐지만 목록을 새로 불러오지 못했습니다. 새로고침해 주세요."); }
      }
    } finally { setBusy(false); }
  };
  return <section className="projectTreeManager" aria-label="현장 경로 설정">
    <div className="projectSettings__sectionTitle"><h3>현장 경로</h3><span>등록된 경로 {registered.length}개</span></div>
    {onSelectPath ? null : <ProjectTreeImageManager projectId={projectId} projectName={projectName} nodes={nodes} paths={registered}
      onRegistered={onRegistered} disabled={disabled || busy} />}
    {onSelectPath && <details>
      <summary>새 경로 등록</summary>
      <ProjectTreePathEditor paths={paths} onChange={setPaths} nodes={nodes} disabled={disabled || busy} />
      <button type="button" className="btn-bg-blue" onClick={save}
        disabled={disabled || busy || !normalizeProjectPaths(paths).length}>{busy ? "등록 중…" : "경로 등록"}</button>
    </details>}
    {message && <p role="status" className="projectSettings__message">{message}</p>}
  </section>;
}
